/** Maps a project's side, appointment, form of contract and named contacts between rows and the Project type. These fields were silently dropped on save before; keeping the mapping in one tested place stops that recurring. */

import { appointmentIsValidForSide, normaliseActingFor, normaliseAppointment } from '../../contract-administration/roleProfile';
import type { ActingFor, EmployerAppointment } from '../../contract-administration/types';

type Row = Record<string, unknown>;

function text(row: Row, snake: string, camel: string): string | undefined {
  const value = row[snake] ?? row[camel];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function projectRoleFieldsFromBackend(row: Row): {
  actingFor?: ActingFor;
  appointment?: EmployerAppointment | null;
  formOfContract?: string;
  mainContractorEstimatorId?: string;
  mainContractorSiteManagerId?: string;
  mainContractorPqsId?: string;
  clientProjectManagerId?: string;
} {
  // Absent column (database not yet migrated): report nothing rather than a
  // default, so transformProjectForBackend does not send acting_for back.
  const hasSide = 'acting_for' in row || 'actingFor' in row;
  const actingFor = hasSide ? normaliseActingFor(row.acting_for ?? row.actingFor) : undefined;
  return {
    actingFor,
    appointment: actingFor === undefined ? undefined : normaliseAppointment(row.appointment),
    formOfContract: text(row, 'form_of_contract', 'formOfContract'),
    mainContractorEstimatorId: text(row, 'main_contractor_estimator_id', 'mainContractorEstimatorId'),
    mainContractorSiteManagerId: text(row, 'main_contractor_site_manager_id', 'mainContractorSiteManagerId'),
    mainContractorPqsId: text(row, 'main_contractor_pqs_id', 'mainContractorPqsId'),
    clientProjectManagerId: text(row, 'client_project_manager_id', 'clientProjectManagerId'),
  };
}

const NULLABLE_TEXT_FIELDS: ReadonlyArray<[camel: string, snake: string]> = [
  ['formOfContract', 'form_of_contract'],
  ['mainContractorEstimatorId', 'main_contractor_estimator_id'],
  ['mainContractorSiteManagerId', 'main_contractor_site_manager_id'],
  ['mainContractorPqsId', 'main_contractor_pqs_id'],
  ['clientProjectManagerId', 'client_project_manager_id'],
];

export function projectRoleFieldsForBackend(project: Row): Row {
  const out: Row = {};
  if (project.actingFor !== undefined) {
    const actingFor = normaliseActingFor(project.actingFor);
    out.acting_for = actingFor;
    const appointment = normaliseAppointment(project.appointment);
    out.appointment = appointment && appointmentIsValidForSide(actingFor, appointment) ? appointment : null;
  }
  for (const [camel, snake] of NULLABLE_TEXT_FIELDS) {
    if (!(camel in project) || project[camel] === undefined) continue;
    const value = project[camel];
    out[snake] = typeof value === 'string' && value.length > 0 ? value : null;
  }
  return out;
}
